import { inject, Injectable, InjectionToken } from '@angular/core';
import { MessageService, ToastMessageOptions } from 'primeng/api';

/**
 * Time window (ms) during which an identical toast is shown only once.
 *
 * A burst of concurrent request failures that all produce the same message
 * (offline, a 500, the same validation error, ...) collapses to a single toast;
 * distinct messages are unaffected. The default sits just above PrimeNG's
 * default toast life (3 s) so a repeat can't appear while the first is still on
 * screen. Override per-environment with a provider if needed:
 *   { provide: TOAST_DEDUP_WINDOW_MS, useValue: 6_000 }
 */
export const TOAST_DEDUP_WINDOW_MS = new InjectionToken<number>(
  'TOAST_DEDUP_WINDOW_MS',
  { providedIn: 'root', factory: () => 4_000 }
);

/**
 * Drop-in replacement for PrimeNG's {@link MessageService} that suppresses
 * duplicate toasts within a short window.
 *
 * WHY at the MessageService layer (and not in ToastService): every toast in the
 * app funnels through `messageService.add()` — the shared `ToastService`, the
 * HTTP interceptor, AND the handful of components that call `MessageService`
 * directly — and `<p-toast>` renders from this same instance. Deduping here is
 * therefore the one true choke-point: it works globally, needs zero changes to
 * the ~130 call sites, and can't be bypassed by a future direct caller. Swapped
 * in via `{ provide: MessageService, useClass: DedupMessageService }`.
 *
 * Only `error`/`warn` participate — those are the severities that arrive in
 * identical bursts from failed requests. `success`/`info` pass through untouched
 * so intentional repeats (e.g. a per-item "saved") still surface.
 *
 * Concurrency: JavaScript is single-threaded, so the check-and-set of `recent`
 * below runs without interleaving. The first of N near-simultaneous identical
 * failures is emitted and stamps the map; the rest observe a fresh entry and are
 * dropped. No lock is needed. In-memory only, bounded + TTL-evicted so a
 * long-lived session can't leak.
 */
@Injectable()
export class DedupMessageService extends MessageService {
  private readonly windowMs = inject(TOAST_DEDUP_WINDOW_MS);

  /** Severities eligible for suppression. Widen here if you also want to dedupe info/success. */
  private readonly dedupedSeverities = new Set<string>(['error', 'warn']);

  /** dedup-key -> last-emitted timestamp (ms). */
  private readonly recent = new Map<string, number>();
  private readonly maxEntries = 200;

  override add(message: ToastMessageOptions): void {
    if (!this.isDuplicate(message)) {
      super.add(message);
    }
  }

  override addAll(messages: ToastMessageOptions[]): void {
    const fresh = messages.filter((m) => !this.isDuplicate(m));
    if (fresh.length) {
      super.addAll(fresh);
    }
  }

  /**
   * True when an identical toast was already emitted within the window. Records
   * fresh (non-duplicate) messages so the next identical one is caught.
   */
  private isDuplicate(message: ToastMessageOptions): boolean {
    const severity = message?.severity ?? 'info';
    if (!this.dedupedSeverities.has(severity)) {
      return false;
    }

    // `key` (toast group/outlet) is part of identity: the same text sent to two
    // different outlets is not a duplicate.
    const key = [
      message.key ?? '',
      severity,
      message.summary ?? '',
      message.detail ?? '',
    ].join('|');

    const now = Date.now();
    this.evictExpired(now);

    const last = this.recent.get(key);
    if (last !== undefined && now - last < this.windowMs) {
      return true;
    }

    this.recent.set(key, now);
    if (this.recent.size > this.maxEntries) {
      const oldest = this.recent.keys().next().value;
      if (oldest !== undefined) this.recent.delete(oldest);
    }
    return false;
  }

  private evictExpired(now: number): void {
    for (const [key, ts] of this.recent) {
      if (now - ts >= this.windowMs) this.recent.delete(key);
    }
  }
}
